import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { RequirePatientAuth } from '@/components/auth/RequirePatientAuth'
import { RequireStaffAuth } from '@/components/auth/RequireStaffAuth'
import { DEV_FAKE_STAFF } from '@/config'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useSession } from '@/lib/auth-client'
import { PatientPortalIndex } from '@/routes/appointments/index'
import { PatientLoginPage } from '@/routes/appointments/login'
import { PatientAppointmentsPage } from '@/routes/appointments/viewer'
import { CalendarPage } from '@/routes/calendar'
import { HomePage } from '@/routes/home'
import { LoginPage } from '@/routes/login'
import { PatientPage } from '@/routes/patient'
import { SettingsPage } from '@/routes/settings'
import { FontSizeSettingsPage } from '@/routes/settings/font-size'
import { PdfTextsSettingsPage } from '@/routes/settings/pdf-texts'
import { SmsRemindersSettingsPage } from '@/routes/settings/sms-reminders'
import { WeeklyReportSettingsPage } from '@/routes/settings/weekly-report'
import { VoiceAgentPage } from '@/routes/voice-agent'

function useDocumentTitle() {
	const location = useLocation()

	useEffect(() => {
		const path = location.pathname
		let suffix = ''

		if (path.startsWith('/calendar/zub')) {
			suffix = ' — Zubní ordinace'
		} else if (path.startsWith('/calendar/hyg')) {
			suffix = ' — Dentální hygiena'
		} else if (path.startsWith('/appointments')) {
			suffix = ' — Moje objednávky'
		}

		document.title = `Dentaly${suffix}`
	}, [location.pathname])
}

/**
 * Root route: staff with a session get the home page; anonymous visitors
 * land on the public patient portal instead of the staff login.
 */
function RootRoute() {
	const { data: session, isPending } = useSession()

	// Dev-only bypass; compiled out of production builds (see config.ts)
	if (DEV_FAKE_STAFF) {
		return <HomePage />
	}

	if (isPending) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	if (!session) {
		return <Navigate to="/appointments" replace />
	}

	return <HomePage />
}

function NotFound() {
	return (
		<div className="flex min-h-screen items-center justify-center">
			<div className="text-center">
				<h1 className="text-4xl font-bold">404</h1>
				<p className="mt-2 text-muted-foreground">Page not found</p>
			</div>
		</div>
	)
}

export default function App() {
	useDocumentTitle()

	return (
		<TooltipProvider>
			<Routes>
				{/* Part A — public patient portal */}
				<Route path="/appointments/login" element={<PatientLoginPage />} />
				<Route element={<RequirePatientAuth />}>
					<Route path="/appointments" element={<PatientPortalIndex />} />
					<Route
						path="/appointments/:patientId"
						element={<PatientAppointmentsPage />}
					/>
				</Route>

				{/* Part B — staff */}
				<Route path="/login" element={<LoginPage />} />
				<Route path="/" element={<RootRoute />} />
				<Route element={<RequireStaffAuth />}>
					<Route
						path="/calendar"
						element={<Navigate to="/calendar/zub" replace />}
					/>
					<Route path="/calendar/:type" element={<CalendarPage />} />
					<Route path="/patient/:id" element={<PatientPage />} />
					<Route path="/voice-agent" element={<VoiceAgentPage />} />
					<Route path="/settings" element={<SettingsPage />} />
					<Route
						path="/settings/font-size"
						element={<FontSizeSettingsPage />}
					/>
					<Route
						path="/settings/weekly-report"
						element={<WeeklyReportSettingsPage />}
					/>
					<Route
						path="/settings/pdf-texts"
						element={<PdfTextsSettingsPage />}
					/>
					<Route
						path="/settings/sms-reminders"
						element={<SmsRemindersSettingsPage />}
					/>
				</Route>

				<Route path="*" element={<NotFound />} />
			</Routes>
			<Toaster closeButton position="top-center" />
		</TooltipProvider>
	)
}
